/* WC — Desktop: autoscroll thumb rail to THEME-active thumb (v12.2 NO-YANK)
   - Desktop only (mobile handled elsewhere)
   - Follows theme active markers
   - HARD RULES:
     (1) Never scroll the document vertically (NO scrollIntoView fallback)
     (2) Only run while the HERO gallery is still in/near the viewport
     (3) Once user scrolls past the hero zone, permanently disable and disconnect observers
*/

(() => {
  if (window.matchMedia('(max-width: 959px)').matches) return;

  const onReady = (fn) => {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
  };

  const visible = (el) => !!(el && el.getClientRects && el.getClientRects().length);
  const dbg = (...a) => window.WC_DESK_THUMB_DEBUG && console.log('[WC DeskThumb]', ...a);

  const ACTIVE_SEL = [
    '.product-thumbnails__item-link[aria-current="true"]',
    '.product-thumbnails__item-link[aria-current="page"]',
    'button[aria-current="true"]',
    'a[aria-current="true"]',
    'button[aria-current="page"]',
    'a[aria-current="page"]',
    '.product-thumbnails__item.is-active',
    '.product-thumbnails__item.active',
    '.product-thumbnails__item.current',
    '.product-thumbnails__item--active',
    '.product-thumbnails__item-link.is-active',
    '.product-thumbnails__item-link.active',
    '.product-thumbnails__item-link.current',
    '.product-thumbnails__item-link[aria-selected="true"]',
    'button[aria-selected="true"]',
    'a[aria-selected="true"]',
  ].join(',');

  // Hero selector: must match your actual gallery container
  const HERO_SEL = [
    '.product-media',
    '[data-product-gallery]',
    '.product__media',
    '.product__media-wrapper',
    '.product__gallery'
  ].join(',');

  onReady(() => {
    const root =
      document.querySelector('#wc-product-root[data-section-type="product"]') ||
      document.querySelector('[data-section-type="product"]');
    if (!root) return;

    const hero = root.querySelector(HERO_SEL);

    const rails = Array.from(
      root.querySelectorAll('.product-thumbnails[data-product-thumbnails], .product-thumbnails')
    ).filter(visible);

    if (!rails.length) return;

    const getViewport = (rail) => (
      rail.querySelector('.product-thumbnails__viewport[data-scroll-container-viewport]') ||
      rail.querySelector('[data-scroll-container-viewport]') ||
      rail.querySelector('.product-thumbnails__viewport') ||
      rail
    );

    const heroInViewport = () => {
      if (!hero) {
        // Fallback: if no hero found, restrict to top-of-page only
        return (window.scrollY || 0) < 220;
      }
      const r = hero.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      // Consider "in play" if hero intersects viewport or is just above it slightly
      return r.bottom > 80 && r.top < vh;
    };

    let disabled = false;
    const timeouts = new Set();
    const observers = [];

    const disableForever = (why) => {
      if (disabled) return;
      disabled = true;
      dbg('DISABLED', why);
      // kill pending timers
      timeouts.forEach(id => clearTimeout(id));
      timeouts.clear();
      // disconnect observers
      observers.forEach(mo => { try { mo.disconnect(); } catch(e) {} });
      observers.length = 0;
      // remove listeners (best effort)
      window.removeEventListener('scroll', onUserScroll, true);
      root.removeEventListener('variant:change', onVariant, true);
      document.removeEventListener('variant:change', onVariant, true);
    };

    // If user scrolls past hero zone, stop autoscroll permanently
    const onUserScroll = () => {
      if (!heroInViewport() && (window.scrollY || 0) > 240) {
        disableForever('user-left-hero');
      }
    };
    window.addEventListener('scroll', onUserScroll, { passive: true, capture: true });

    const findActiveInRail = (rail) => {
      const hit = rail.querySelector(ACTIVE_SEL);
      if (!hit) return null;
      const wrap = hit.classList?.contains('product-thumbnails__item')
        ? hit
        : hit.closest?.('.product-thumbnails__item');
      return wrap || null;
    };

    const isInRailView = (vp, wrap) => {
      const sr = vp.getBoundingClientRect();
      const r = wrap.getBoundingClientRect();
      const pad = 10;
      return r.left >= sr.left + pad && r.right <= sr.right - pad;
    };

    const scrollActiveIntoCenter = (vp, active, behavior) => {
      const vpRect = vp.getBoundingClientRect();
      const aRect = active.getBoundingClientRect();

      const activeCenterX = (aRect.left - vpRect.left) + vp.scrollLeft + (aRect.width / 2);
      const targetLeft = Math.max(0, activeCenterX - (vp.clientWidth / 2));

      const maxLeft = Math.max(0, vp.scrollWidth - vp.clientWidth);
      const clamped = Math.min(maxLeft, targetLeft);

      try {
        vp.scrollTo({ left: clamped, behavior });
      } catch (e) {
        vp.scrollLeft = clamped;
      }
    };

    let raf = 0;
    let lastKey = null;
    let cooldownUntil = 0;
    let firstRun = true;

    const doScroll = (reason) => {
      if (disabled) return;

      // HARD GATE: never run once hero zone is gone
      if (!heroInViewport()) {
        // If user already started scrolling away, disable forever to prevent late bursts yanking
        if ((window.scrollY || 0) > 240) disableForever('hero-offscreen');
        return;
      }

      const now = performance.now();
      if (now < cooldownUntil) return;

      // pick first rail that has an active thumb
      let rail = null;
      let active = null;
      for (const r of rails) {
        const a = findActiveInRail(r);
        if (a && visible(a)) { rail = r; active = a; break; }
      }
      if (!rail || !active) return;

      const vp = getViewport(rail);

      const i = Array.from(rail.querySelectorAll('.product-thumbnails__item')).indexOf(active);
      const link = active.querySelector('.product-thumbnails__item-link,button,a');
      const key = `${i}|${link?.getAttribute?.('data-thumbnail-index') || ''}|${link?.getAttribute?.('data-thumbnail-id') || ''}|${link?.getAttribute?.('aria-current') || ''}`;

      if (key === lastKey && isInRailView(vp, active)) return;
      lastKey = key;

      if (isInRailView(vp, active)) return;

      const behavior = (firstRun || reason === 'init') ? 'auto' : 'smooth';
      firstRun = false;

      dbg('scrollLeft only', reason, { i, behavior });
      scrollActiveIntoCenter(vp, active, behavior);

      cooldownUntil = performance.now() + 160;
    };

    const schedule = (reason) => {
      if (disabled) return;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => doScroll(reason));
    };

    const burst = (reason) => {
      // Shorter burst: enough for theme late-active flips, but avoids 1.4s yank window
      [0, 80, 180, 420].forEach(t => {
        const id = setTimeout(() => schedule(reason), t);
        timeouts.add(id);
      });
    };

    // INIT
    burst('init');
    window.addEventListener('load', () => burst('load'), { once: true });
    window.addEventListener('pageshow', (e) => { if (e.persisted) burst('pageshow'); });

    // Variant change events
    const onVariant = () => burst('variant');
    root.addEventListener('variant:change', onVariant, true);
    document.addEventListener('variant:change', onVariant, true);

    // Option changes fallback
    root.addEventListener('change', (e) => {
      const n = e.target?.getAttribute?.('name') || '';
      if (/^options\[/.test(n)) onVariant();
    }, true);

    // Observe rails for active flips (safe-gated by heroInViewport)
    for (const rail of rails) {
      const mo = new MutationObserver(() => schedule('rail-mutation'));
      mo.observe(rail, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'aria-current', 'aria-selected']
      });
      observers.push(mo);
    }

    window.WC_DeskThumb = { syncNow: () => schedule('manual') };
  });
})();